import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '@/lib/api';
import { ArrowLeft, Stethoscope, Clock, Share2, Newspaper } from 'lucide-react';

// Very lightweight markdown renderer (headings, bullets, paragraphs, bold)
function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split('\n');
  const blocks = [];
  let list = null;
  let para = [];

  const flushPara = () => {
    if (para.length) {
      blocks.push({ type: 'p', text: para.join(' ') });
      para = [];
    }
  };
  const flushList = () => {
    if (list) { blocks.push({ type: 'ul', items: list }); list = null; }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushPara(); flushList(); continue; }
    if (line.startsWith('## ')) { flushPara(); flushList(); blocks.push({ type: 'h2', text: line.slice(3) }); continue; }
    if (line.startsWith('### ')) { flushPara(); flushList(); blocks.push({ type: 'h3', text: line.slice(4) }); continue; }
    if (line.startsWith('# ')) { flushPara(); flushList(); blocks.push({ type: 'h2', text: line.slice(2) }); continue; }
    if (line.startsWith('- ') || line.startsWith('* ')) {
      flushPara();
      if (!list) list = [];
      list.push(line.slice(2));
      continue;
    }
    flushList();
    para.push(line);
  }
  flushPara(); flushList();

  const inline = (t) => {
    // bold **text**
    const parts = t.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : <React.Fragment key={i}>{p}</React.Fragment>);
  };

  return blocks.map((b, i) => {
    if (b.type === 'h2') return <h2 key={i} className="text-2xl sm:text-3xl font-medium text-slate-900 mt-10 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{inline(b.text)}</h2>;
    if (b.type === 'h3') return <h3 key={i} className="text-xl font-medium text-slate-900 mt-6 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{inline(b.text)}</h3>;
    if (b.type === 'ul') return <ul key={i} className="list-disc pl-6 my-4 space-y-2 text-slate-700">{b.items.map((li, j) => <li key={j}>{inline(li)}</li>)}</ul>;
    return <p key={i} className="text-slate-700 leading-relaxed my-4">{inline(b.text)}</p>;
  });
}

export default function DoctorArticle() {
  const { slug, articleSlug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [error, setError] = useState('');
  const [related, setRelated] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/public/doctor/${slug}/articles/${articleSlug}`)
      .then((r) => { if (!cancelled) { setArticle(r.data.article); setDoctor(r.data.doctor); } })
      .catch(() => { if (!cancelled) setError('Article not found'); });

    // Lead tracking
    api.post('/track/event', {
      doctor_slug: slug,
      event_type: 'page_view',
      referrer: document.referrer || '',
    }).catch(() => {});

    // Related content for internal linking
    api.get(`/public/doctor/${slug}/related-content`)
      .then(r => { if (!cancelled) setRelated(r.data); })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [slug, articleSlug]);

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <div className="text-center">
          <p className="text-slate-500 mb-4">This article is no longer available.</p>
          <Link to={`/dr/${slug}`} className="text-sky-600 hover:text-sky-700 text-sm font-medium">
            Back to doctor profile
          </Link>
        </div>
      </div>
    );
  }

  if (!article || !doctor) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400 animate-pulse">Loading...</div>;
  }

  const canonicalUrl = `${window.location.origin}/dr/${slug}/articles/${article.slug}`;
  const articlesIndexUrl = `${window.location.origin}/dr/${slug}/articles`;
  const doctorUrl = `${window.location.origin}/dr/${slug}`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.meta_description,
    author: { '@type': 'Person', name: doctor.name },
    datePublished: article.published_at,
    dateModified: article.updated_at,
    keywords: (article.keywords || []).join(', '),
    mainEntityOfPage: canonicalUrl,
    image: doctor.photo_url || undefined,
    publisher: { '@type': 'Organization', name: 'DocSite' },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: doctor.name, item: doctorUrl },
      { '@type': 'ListItem', position: 2, name: 'Articles', item: articlesIndexUrl },
      { '@type': 'ListItem', position: 3, name: article.title, item: canonicalUrl },
    ],
  };

  const faqJsonLd = article.faq && article.faq.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faq.map(f => ({
      '@type': 'Question', name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  } : null;

  const onShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: article.title, url: canonicalUrl });
      } else {
        await navigator.clipboard.writeText(canonicalUrl);
        alert('Link copied to clipboard');
      }
    } catch {}
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>{article.title}</title>
        <meta name="description" content={article.meta_description} />
        {article.keywords && article.keywords.length > 0 && (
          <meta name="keywords" content={article.keywords.join(', ')} />
        )}
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.meta_description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        {doctor.photo_url && <meta property="og:image" content={doctor.photo_url} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.meta_description} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        {faqJsonLd && <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>}
      </Helmet>

      {/* Top nav */}
      <div className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate(`/dr/${slug}`)} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-sky-600" data-testid="back-to-doctor-btn">
            <ArrowLeft size={14} />Back to {doctor.name}
          </button>
          <button onClick={onShare} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-sky-600" data-testid="share-article-btn">
            <Share2 size={14} />Share
          </button>
        </div>
      </div>

      {/* Article */}
      <article className="max-w-3xl mx-auto px-6 py-10" data-testid="article-body">
        <nav aria-label="breadcrumb" className="text-xs text-slate-500 mb-5 flex items-center gap-1.5 flex-wrap" data-testid="article-breadcrumb">
          <Link to={`/dr/${slug}`} className="hover:text-sky-600">{doctor.name}</Link>
          <span className="text-slate-300">/</span>
          <Link to={`/dr/${slug}/articles`} className="hover:text-sky-600">Articles</Link>
          <span className="text-slate-300">/</span>
          <span className="text-slate-700 truncate max-w-[40ch]">{article.title}</span>
        </nav>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-bold text-sky-600 mb-4">
          <Stethoscope size={12} />
          {article.primary_specialty || 'Medical Insight'}
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-slate-900 tracking-tight leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {article.title}
        </h1>
        <div className="flex items-center gap-3 mt-5 text-sm text-slate-500">
          {doctor.photo_url && <img src={doctor.photo_url} alt={doctor.name} className="w-10 h-10 rounded-full object-cover border border-slate-200" />}
          <div>
            <p className="font-medium text-slate-700">{doctor.name}</p>
            <p className="text-xs flex items-center gap-1.5">
              <Clock size={11} />{article.published_at ? new Date(article.published_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
            </p>
          </div>
        </div>

        <div className="mt-8 prose-article">
          {renderMarkdown(article.content)}
        </div>

        {/* FAQ */}
        {article.faq && article.faq.length > 0 && (
          <section className="mt-12 pt-10 border-t border-slate-200" data-testid="article-faq-section">
            <h2 className="text-2xl sm:text-3xl font-medium text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {article.faq.map((f, i) => (
                <div key={i} className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
                  <h3 className="font-medium text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.question}</h3>
                  <p className="text-slate-700 text-sm leading-relaxed">{f.answer}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* CTA — context-aware: editorial articles route to specialist directory; doctor articles to the doctor */}
        {slug === 'docsite-ai-team' ? (
          (() => {
            // Map specialty display name → professional title + URL slug
            const SPEC_TITLE = {
              'Cardiology': ['Cardiologist', 'cardiology'],
              'Dermatology': ['Dermatologist', 'dermatology'],
              'ENT (Otolaryngology)': ['ENT Specialist', 'ent'],
              'Gastroenterology': ['Gastroenterologist', 'gastroenterology'],
              'Physician (MD Medicine)': ['Physician', 'physician'],
              'General Surgery': ['General Surgeon', 'general_surgery'],
              'Gynecology & Obstetrics': ['Gynecologist', 'gynecology'],
              'Neurology': ['Neurologist', 'neurology'],
              'Neurosurgery': ['Neurosurgeon', 'neurosurgery'],
              'Oncology': ['Oncologist', 'oncology'],
              'Ophthalmology': ['Ophthalmologist', 'ophthalmology'],
              'Orthopedics': ['Orthopedic Specialist', 'orthopedics'],
              'Pediatrics': ['Pediatrician', 'pediatrics'],
              'Psychiatry': ['Psychiatrist', 'psychiatry'],
              'Pulmonology': ['Pulmonologist', 'pulmonology'],
              'Urology': ['Urologist', 'urology'],
              'Dentistry': ['Dentist', 'dentistry'],
              'Endocrinology': ['Endocrinologist', 'endocrinology'],
              'Nephrology': ['Nephrologist', 'nephrology'],
              'Rheumatology': ['Rheumatologist', 'rheumatology'],
              'Plastic & Cosmetic Surgery': ['Plastic Surgeon', 'plastic_surgery'],
              'Allergy & Immunology': ['Allergy & Immunology Specialist', 'allergy_immunology'],
              'Radiology': ['Radiologist', 'radiology'],
              'Anesthesiology': ['Anesthesiologist', 'anesthesiology'],
              'Physiotherapy': ['Physiotherapist', 'physiotherapy'],
              'Diabetology': ['Diabetologist', 'diabetology'],
              'Infectious Disease': ['Infectious Disease Specialist', 'infectious_disease'],
              'Vascular Surgery': ['Vascular Surgeon', 'vascular_surgery'],
              'Pain Management': ['Pain Management Specialist', 'pain_management'],
            };
            const [title, specSlug] = SPEC_TITLE[article.primary_specialty] || ['Specialist', article.specialty_slug || ''];
            return (
              <section className="mt-12 bg-gradient-to-r from-sky-50 to-emerald-50 border border-sky-100 rounded-2xl p-7 sm:p-9" data-testid="article-cta">
                <h3 className="text-2xl sm:text-3xl font-medium text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Book a consult with your Specialist {title}
                </h3>
                <p className="text-base text-slate-600 mb-6 leading-relaxed">
                  Find practising {title.toLowerCase()}s on DocSite and book directly — no middlemen, no paid rankings.
                </p>
                <Link to={specSlug ? `/find/${specSlug}` : '/find-all-specialties'}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-sky-600 text-white rounded-xl text-base font-medium hover:bg-sky-700"
                      data-testid="article-find-specialist-btn">
                  Find a {title}
                </Link>
              </section>
            );
          })()
        ) : (
          <section className="mt-12 bg-gradient-to-r from-sky-50 to-emerald-50 border border-sky-100 rounded-2xl p-7 sm:p-9" data-testid="article-cta">
            <h3 className="text-2xl sm:text-3xl font-medium text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Book a consultation with {doctor.name}
            </h3>
            <p className="text-base text-slate-600 mb-6 leading-relaxed">
              Discuss your concerns directly with an experienced {(doctor.specializations || []).length ? 'specialist' : 'doctor'}.
            </p>
            <Link to={`/dr/${slug}`} className="inline-flex items-center gap-2 px-6 py-3 bg-sky-600 text-white rounded-xl text-base font-medium hover:bg-sky-700" data-testid="article-book-btn">
              View profile & book
            </Link>
          </section>
        )}
      </article>

      {/* Related Content — Internal Linking for SEO */}
      {related && (
        <ArticleRelatedContent
          related={related}
          currentSlug={articleSlug}
          doctorSlug={slug}
          doctorName={doctor.name}
        />
      )}

      <footer className="border-t border-slate-200 mt-16 py-8 text-center text-xs text-slate-400">
        Published on <Link to={`/dr/${slug}`} className="text-sky-600 hover:underline">{doctor.name}'s DocSite</Link>
      </footer>
    </div>
  );
}


function ArticleRelatedContent({ related, currentSlug, doctorSlug, doctorName }) {
  const otherArticles = (related.articles || []).filter(a => a.slug !== currentSlug).slice(0, 4);
  const procedures = (related.procedures || []).slice(0, 4);

  if (!otherArticles.length && !procedures.length) return null;

  return (
    <section className="py-12 bg-slate-50/60 border-y border-slate-100 mt-8" data-testid="related-content-section">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <h2 className="text-xl sm:text-2xl font-medium text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
          More from {doctorName}
        </h2>

        {procedures.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xs uppercase tracking-[0.15em] font-bold text-sky-600 mb-3 flex items-center gap-1.5">
              <Stethoscope size={12} /> Procedures by {doctorName}
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {procedures.map(p => (
                <Link key={p.procedure_slug} to={`/dr/${doctorSlug}/procedures/${p.procedure_slug}`}
                  className="block bg-white rounded-xl border border-slate-200 p-4 hover:border-sky-200 hover:shadow-sm transition-all"
                  data-testid={`related-proc-${p.procedure_slug}`}>
                  <p className="text-sm font-medium text-slate-900">{p.procedure_name}</p>
                  {p.meta_description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{p.meta_description}</p>}
                </Link>
              ))}
            </div>
          </div>
        )}

        {otherArticles.length > 0 && (
          <div>
            <h3 className="text-xs uppercase tracking-[0.15em] font-bold text-sky-600 mb-3 flex items-center gap-1.5">
              <Newspaper size={12} /> More Articles
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {otherArticles.map(a => (
                <Link key={a.slug} to={`/dr/${doctorSlug}/articles/${a.slug}`}
                  className="block bg-white rounded-xl border border-slate-200 p-4 hover:border-sky-200 hover:shadow-sm transition-all"
                  data-testid={`related-article-${a.slug}`}>
                  <p className="text-sm font-medium text-slate-900">{a.title}</p>
                  {a.meta_description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{a.meta_description}</p>}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
